import { ModalityType } from '@littlebird/sdk';
import { getTurnActivityLabel, useRollingEntry } from '@littlebird/ui-utils';
import { findLast } from 'lodash';
import {
  AnimatePresence,
  motion,
  MotionConfig,
  useReducedMotion,
} from 'motion/react';
import { useState, type ReactNode } from 'react';

import { Icons } from '@/assets/icons/icons';
import type {
  TurnActivityChildItem,
  TurnActivityEntry,
} from '@/components/blocks/Message/MessageList/chatItems.types';
import {
  ReasoningStepList,
  StepPreviewContent,
} from '@/components/blocks/Message/reasoning';
import { isArtifactWriteModality } from '@/components/blocks/Message/reasoning/artifact-preview';
import { ReasoningStepType } from '@/components/blocks/Message/reasoning/reasoning.types';
import { YStack } from '@/components/ui';
import { loaderPortalId } from '@/components/ui/logo-loader';
import { EASE } from '@/lib/animate';
import { cn } from '@/lib/utils';

import { SlotMachineText } from './SlotMachineText';

interface TurnActivityPanelProps {
  id: string;
  items: TurnActivityChildItem[];
  entries: TurnActivityEntry[];
  isActive: boolean;
  isCompact?: boolean;
  forceExpanded?: boolean;
  renderItem: (item: TurnActivityChildItem, index: number) => ReactNode;
}

export const TurnActivityPanel = ({
  id,
  items,
  entries,
  isActive,
  isCompact = false,
  forceExpanded = false,
  renderItem,
}: TurnActivityPanelProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCollapsing, setIsCollapsing] = useState(false);
  const isOpen = isExpanded || forceExpanded;
  const rollingEntry = useRollingEntry(entries, isActive);
  const shouldReduceMotion = useReducedMotion();

  const loneTrace =
    items.length === 1 && items[0].type === 'reasoning' ? items[0] : undefined;

  const stepWord = entries.length === 1 ? 'step' : 'steps';
  const summaryLabel = isActive
    ? `${getTurnActivityLabel(entries.length, id)}…`
    : `Worked for ${entries.length} ${stepWord}`;
  const liveEntry =
    isActive && !isOpen && !isCollapsing ? rollingEntry : undefined;

  const artifactStep = liveEntry
    ? findLast(
        entries,
        ({ step }) =>
          step?.type === ReasoningStepType.Modality &&
          isArtifactWriteModality(step.modality)
      )?.step
    : undefined;
  const isSubagentActivity =
    liveEntry?.step?.type === ReasoningStepType.Modality &&
    liveEntry.step.modality.type === ModalityType.RunSubagent;
  const previewStep =
    artifactStep ?? (isSubagentActivity ? undefined : liveEntry?.step);

  return (
    <MotionConfig reducedMotion="user">
      <YStack
        data-message-id={id}
        data-testid="turn-activity-panel"
        className="relative w-full py-3"
      >
        <div
          id={loaderPortalId(id)}
          className="pointer-events-none absolute -left-1.5 top-2.5 size-6"
        />
        <div className={cn({ 'px-6': !isCompact })}>
          <button
            type="button"
            data-testid="turn-activity-header"
            aria-expanded={isOpen}
            onClick={() => {
              setIsCollapsing(isExpanded);
              setIsExpanded(!isExpanded);
            }}
            className={cn(
              'group/turn flex w-full flex-col items-start gap-0.5 rounded px-1 text-left select-none cursor-pointer',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-strong'
            )}
          >
            <span className="flex items-center gap-1 text-sm">
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.span
                  key={summaryLabel}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18, ease: EASE.outQuint }}
                  className="text-[var(--reasoning-accent)]"
                >
                  {summaryLabel}
                </motion.span>
              </AnimatePresence>
              <Icons.chevronRight
                className={cn(
                  'size-3 shrink-0 text-foreground-subtle',
                  'transition-[opacity,transform] duration-150 ease-out-quint',
                  'motion-reduce:transition-none',
                  'opacity-0 group-hover/turn:opacity-100 group-focus-visible/turn:opacity-100',
                  isOpen && 'rotate-90 opacity-100'
                )}
              />
            </span>
            {liveEntry && !artifactStep && (
              <SlotMachineText text={liveEntry.label} />
            )}
          </button>
          {previewStep && (
            <AnimatePresence mode="wait">
              <motion.div
                key={previewStep.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.12, ease: EASE.outQuint }}
                className="pl-1"
              >
                <StepPreviewContent step={previewStep} />
              </motion.div>
            </AnimatePresence>
          )}
        </div>

        <AnimatePresence onExitComplete={() => setIsCollapsing(false)}>
          {isOpen && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: 'auto' }}
              exit={{
                height: 0,
                transition: {
                  duration: shouldReduceMotion ? 0 : 0.15,
                  ease: EASE.outQuint,
                },
              }}
              transition={{
                duration: shouldReduceMotion ? 0 : 0.22,
                ease: EASE.outQuint,
              }}
              className="overflow-hidden"
            >
              <YStack>
                {loneTrace ? (
                  <div className={cn('py-3', { 'px-6': !isCompact })}>
                    <ReasoningStepList
                      steps={loneTrace.steps}
                      status={loneTrace.status}
                    />
                  </div>
                ) : (
                  items.map(renderItem)
                )}
              </YStack>
            </motion.div>
          )}
        </AnimatePresence>
      </YStack>
    </MotionConfig>
  );
};
